import React, { useCallback, useEffect, useState } from 'react';
import { Search, SearchX, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SearchAnalyticsCard() {
  const token = sessionStorage.getItem('admin_token');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/search-analytics?days=30`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('load failed');
      setData(await res.json());
    } catch (e) { setErr(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (err) return null;
  if (!data) return <div className="bg-white rounded-xl border border-gray-100 p-5"><RefreshCw className="w-4 h-4 text-gray-400 animate-spin" /></div>;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="search-analytics-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Search className="w-4 h-4 text-indigo-500" /> On-Site Search — Last 30 Days
        </h3>
        <button onClick={load} className="text-gray-400 hover:text-gray-600" data-testid="search-analytics-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-4">What visitors type into the search bar — no-result searches are your gap list for gyms and topics to add next.</p>

      <div className="grid grid-cols-3 gap-3 mb-5">
        {[[data.total_searches, 'Searches'], [data.unique_queries, 'Unique queries'], [`${data.zero_rate}%`, 'No results']].map(([v, label]) => (
          <div key={label} className="bg-gray-50 rounded-lg p-3" data-testid={`search-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <p className="text-lg font-bold text-gray-900">{v}</p>
            <p className="text-[11px] text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div data-testid="search-top-queries">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Top Searches</p>
          {data.top.length === 0 ? (
            <p className="text-xs text-gray-400">No searches yet.</p>
          ) : data.top.slice(0, 12).map((r) => (
            <div key={r.q} className="flex items-center justify-between py-1.5 border-b border-gray-50 text-sm">
              <span className="text-gray-800 truncate">{r.q}</span>
              <span className="flex items-center gap-2 flex-shrink-0">
                {r.zero && <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-500 rounded">0 results</span>}
                <span className="text-gray-500 tabular-nums">{r.searches}</span>
              </span>
            </div>
          ))}
        </div>
        <div data-testid="search-zero-queries">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <SearchX className="w-3.5 h-3.5 text-red-400" /> No-Result Searches (Content Gaps)
          </p>
          {data.zero_top.length === 0 ? (
            <p className="text-xs text-gray-400">Everything searched had results.</p>
          ) : data.zero_top.slice(0, 12).map((r) => (
            <div key={r.q} className="flex items-center justify-between py-1.5 border-b border-gray-50 text-sm">
              <span className="text-gray-800 truncate">{r.q}</span>
              <span className="text-gray-500 tabular-nums flex-shrink-0">{r.searches}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
